'use client';
import heroStyles from '@/components/public/public-hero.module.css';
import { PREORDER_NOTICE } from '@/lib/commerce/preorder-policy';

import Image from 'next/image';
import Link from 'next/link';
import { ArrowDown, BookOpen, Lock, Mail, Sprout, UserRound } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';

import { NewsletterLeadForm } from '@/components/public/newsletter-lead-form';
import { useHomepageSettings } from '@/hooks/use-homepage-settings';
import { Button } from '@/components/ui/button';
import { mergeCatalogRecords } from '@/hooks/use-catalog-page';
import {
  SHOP_SORT_OPTIONS,
  createShopViewModel,
  filterShopProducts,
  formatShopMoney,
  sortShopProducts,
  type ShopBenefitItem,
  type ShopCategoryId,
  type ShopProduct,
  type ShopSortKey,
  type ShopViewModel,
} from '@/lib/platform/shop';
import { ROUTES } from '@/lib/routes';
import type { HomepageSettingsDoc, PlatformCollectionDoc, TitleDoc } from '@/types/platform';

function ShopImage({
  src,
  alt,
  className = '',
  sizes = '100vw',
  priority = false,
}: {
  src?: string;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
}) {
  const [failed, setFailed] = useState(false);
  if (!src || failed) {
    return (
      <div className={`flex h-full w-full items-center justify-center bg-[radial-gradient(circle_at_30%_20%,rgba(245,220,201,.85),transparent_34%),linear-gradient(135deg,#f8eee4,#e6d4bf)] ${className}`}>
        <Sprout className="h-10 w-10 text-[#b8735c]/55" strokeWidth={1.3} />
      </div>
    );
  }
  return <Image src={src} alt={alt} fill sizes={sizes} priority={priority} className={`object-cover object-center ${className}`} onError={() => setFailed(true)} />;
}

function ShopHero({ view }: { view: ShopViewModel }) {
  return (
    <section className={`${heroStyles.frame} border-b border-[#e9d8cb] bg-[#fbf8f3]`} data-public-hero data-scenery-surface>
      <div className={heroStyles.backdrop} data-scenery-hero>
        <ShopImage src={view.hero.imageUrl} alt={view.hero.imageAlt} priority sizes="100vw" />
        <div className={heroStyles.overlay} />
      </div>
      <div className={`${heroStyles.content} flex`}>
        <div className="max-w-[520px]">
          <p className="text-[12px] font-bold uppercase tracking-[0.28em] text-[#8d4e39]">{view.hero.eyebrow}</p>
          <h1 className="mt-5 font-headline text-[3.35rem] leading-[1.04] tracking-[-0.035em] text-[#2f2d2a] sm:text-[4rem]">{view.hero.title}</h1>
          <p className="mt-6 max-w-[460px] text-[16px] leading-8 text-[#4f4942]">{view.hero.description}</p>
          {view.hero.ctaHref && view.hero.ctaLabel ? <Link href={view.hero.ctaHref} className="mt-7 inline-flex h-12 items-center justify-center rounded-[.55rem] bg-[#547a5f] px-7 text-sm font-semibold text-white shadow-sm transition hover:bg-[#496d53]">
            {view.hero.ctaLabel}
          </Link> : null}
        </div>
      </div>
    </section>
  );
}

function ShopCategorySidebar({
  view,
  activeCategory,
  onCategoryChange,
}: {
  view: ShopViewModel;
  activeCategory: ShopCategoryId;
  onCategoryChange: (category: ShopCategoryId) => void;
}) {
  return (
    <aside className="space-y-5 lg:sticky lg:top-24">
      <div className="rounded-[1.1rem] border border-[#e7d5c7] bg-[#fffdf9] p-5">
        <h2 className="font-headline text-2xl text-[#2f2d2a]">Browse by category</h2>
      <div className="mt-4 grid gap-1 sm:grid-cols-2 lg:block lg:space-y-1">
          {view.categories.map((category) => (
            <button
              key={category.id}
              type="button"
              onClick={() => onCategoryChange(category.id)}
              className={`block w-full rounded-[.45rem] px-4 py-3 text-left text-[15px] transition ${activeCategory === category.id ? 'bg-[#f3dfd5] text-[#2f2d2a]' : 'text-[#4f4942] hover:bg-[#fbf4ee]'}`}
            >
              {category.label}
            </button>
          ))}
        </div>
      </div>
      <div className="rounded-[1.1rem] border border-[#e7d5c7] bg-[#fffdf9] p-6">
        <Sprout className="h-10 w-10 text-[#d08a70]" strokeWidth={1.25} />
        <h3 className="mt-6 font-headline text-2xl leading-tight text-[#2f2d2a]">Not sure where to start?</h3>
        <p className="mt-4 text-[14px] leading-7 text-[#5e554d]">Take the private 120-item Untangled Patterns Assessment and receive a complete behaviour profile with practical next steps.</p>
        <Link href={ROUTES.assessment} className="mt-5 inline-flex text-sm font-semibold text-[#b8735c] hover:text-[#8a5a45]">Begin the assessment -&gt;</Link>
      </div>
    </aside>
  );
}

function FeaturedCollectionCard({ view }: { view: ShopViewModel }) {
  const firstProduct = view.featuredCollection.products[0];
  return (
    <section className="grid min-h-[310px] overflow-hidden rounded-[1.1rem] bg-[#fffdf9] shadow-[inset_0_0_0_1px_rgba(231,213,199,1)] md:grid-cols-[minmax(0,.85fr)_minmax(0,1.15fr)]">
      <div className="flex flex-col justify-center px-8 py-8">
        <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#b8735c]">Featured collection</p>
        <h2 className="mt-5 font-headline text-[2rem] leading-tight text-[#2f2d2a]">{view.featuredCollection.title}</h2>
        <p className="mt-4 max-w-[260px] text-[15px] leading-7 text-[#5e554d]">{view.featuredCollection.description}</p>
        {view.featuredCollection.ctaLabel ? <Link href={firstProduct?.href || '#shop-books'} className="mt-7 inline-flex h-11 w-fit items-center rounded-[.45rem] bg-[#5f8769] px-5 text-sm font-semibold text-white hover:bg-[#547a5f]">
          {view.featuredCollection.ctaLabel}
        </Link> : null}
      </div>
      <div className="relative min-h-[260px]">
        <ShopImage src={view.featuredCollection.imageUrl} alt={view.featuredCollection.imageAlt} sizes="(min-width: 1024px) 45vw, 100vw" />
      </div>
    </section>
  );
}

function FeaturedBundleCard({ bundle, onAddBundle }: { bundle: NonNullable<ShopViewModel['bundle']> | null; onAddBundle: (product: ShopProduct) => void }) {
  if (!bundle) return null;
  return (
    <aside className="rounded-[1.1rem] border border-[#efd3cc] bg-[#f8ddd6] p-8">
      <p className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#b8735c]">{bundle.eyebrow}</p>
      <h2 className="mt-5 font-headline text-[2.35rem] leading-[1.04] text-[#2f2d2a]">{bundle.title}</h2>
      <p className="mt-5 text-[15px] leading-7 text-[#5e554d]">{bundle.description}</p>
      <p className="mt-6 text-[18px] font-semibold text-[#2f2d2a]">
        {formatShopMoney(bundle.price, bundle.currency)}
        {bundle.compareAtPrice ? <span className="ml-3 text-[15px] font-normal text-[#8c8077] line-through">{formatShopMoney(bundle.compareAtPrice, bundle.currency)}</span> : null}
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        {bundle.product ? <button type="button" onClick={() => onAddBundle(bundle.product as ShopProduct)} className="inline-flex h-10 items-center rounded-[.45rem] bg-[#5f8769] px-5 text-sm font-semibold text-white hover:bg-[#547a5f]">Choose bundle format</button> : null}
        {bundle.product?.href ? <Link href={bundle.product.href} className="inline-flex h-10 items-center rounded-[.45rem] border border-[#d9b6aa] bg-white/70 px-5 text-sm font-semibold text-[#2f2d2a] hover:bg-white">View bundle</Link> : null}
      </div>
      {bundle.product?.discountLabel ? <p className="mt-4 text-sm font-medium text-[#b8735c]">{bundle.product.discountLabel}</p> : bundle.compareAtPrice ? <p className="mt-4 text-sm font-medium text-[#b8735c]">Save {Math.max(0, Math.round((1 - bundle.price / bundle.compareAtPrice) * 100))}%</p> : null}
    </aside>
  );
}

function ShopToolbar({
  visibleCount,
  totalCount,
  sort,
  onSortChange,
}: {
  visibleCount: number;
  totalCount: number;
  sort: ShopSortKey;
  onSortChange: (sort: ShopSortKey) => void;
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <p className="text-sm text-[#5e554d]">Showing {visibleCount ? 1 : 0}-{visibleCount} of {totalCount} loaded shop items</p>
      <label className="flex items-center gap-3 text-sm text-[#5e554d]">
        Sort loaded items by
        <select id="shop-sort" name="shop-sort" value={sort} onChange={(event) => onSortChange(event.target.value as ShopSortKey)} className="h-10 rounded-[.35rem] border border-[#d9c8bb] bg-white px-3 text-sm text-[#2f2d2a] outline-none">
          {SHOP_SORT_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>
    </div>
  );
}

function ShopProductCard({ product, onAddToCart, showCartButton }: { product: ShopProduct; onAddToCart: (product: ShopProduct) => void; showCartButton: boolean }) {
  return (
    <article className="group overflow-hidden rounded-[.8rem] border border-[#e7d5c7] bg-[#fffdf9] shadow-[0_16px_45px_rgba(70,49,35,.035)]">
      <Link href={product.href} className="block">
        <div className="relative aspect-[1.06] overflow-hidden bg-[#efe2d4]">
          <ShopImage src={product.coverUrl} alt={product.coverAlt} sizes="(min-width: 1280px) 22vw, (min-width: 768px) 33vw, 100vw" />
        </div>
      </Link>
      <div className="p-5">
        <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#b8735c]">{product.category}</p>
        <Link href={product.href} className="mt-3 block">
          <h3 className="font-headline text-[1.45rem] leading-[1.12] text-[#2f2d2a] transition group-hover:text-[#b8735c]">{product.title}</h3>
        </Link>
        <p className="mt-2 line-clamp-3 min-h-[4.5rem] text-[14px] leading-6 text-[#5e554d]">{product.description}</p>
        <p className="mt-3 text-sm font-semibold text-[#2f2d2a]">{formatShopMoney(product.price, product.currency)}</p>
        {product.itemType === 'bundle' && product.includedTitleIds?.length ? <p className="mt-2 text-xs font-medium text-[#b8735c]">{product.includedTitleIds.length} books included</p> : null}
        {product.digitalSaleState === 'coming-soon' ? <p className="text-sm font-semibold">Coming soon</p> : null}
        {product.digitalSaleState === 'preorder' ? <p className="text-xs leading-5 text-amber-900">{PREORDER_NOTICE}</p> : null}
        {product.canPurchase && showCartButton ? (
          <button type="button" onClick={() => onAddToCart(product)} className="mt-4 flex h-10 w-full items-center justify-center rounded-[.35rem] bg-[#5f8769] px-4 text-sm font-semibold text-white transition hover:bg-[#547a5f]">
            Choose format
          </button>
        ) : (
          <Link href={product.href} className="mt-4 flex h-10 w-full items-center justify-center rounded-[.35rem] border border-[#d9c8bb] bg-white px-4 text-sm font-semibold text-[#2f2d2a] transition hover:bg-[#fbf4ee]">
            View book
          </Link>
        )}
      </div>
    </article>
  );
}

function ShopProductGrid({ products, onAddToCart }: { products: ShopProduct[]; onAddToCart: (product: ShopProduct) => void }) {
  if (!products.length) {
    return <div className="rounded-[1rem] border border-[#e7d5c7] bg-[#fffdf9] p-8 text-sm text-[#5e554d]">No loaded books match this category. Load more to continue browsing.</div>;
  }
  return (
    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
      {products.map((product) => <ShopProductCard key={product.id} product={product} onAddToCart={onAddToCart} showCartButton={product.canPurchase} />)}
    </div>
  );
}

function benefitIcon(icon: string) {
  if (icon === 'download') return <ArrowDown className="h-6 w-6" strokeWidth={1.4} />;
  if (icon === 'book') return <BookOpen className="h-6 w-6" strokeWidth={1.4} />;
  if (icon === 'lock') return <Lock className="h-6 w-6" strokeWidth={1.4} />;
  if (icon === 'user') return <UserRound className="h-6 w-6" strokeWidth={1.4} />;
  return <Sprout className="h-6 w-6" strokeWidth={1.4} />;
}

function ShopBenefitStrip({ benefits }: { benefits: ShopBenefitItem[] }) {
  return (
    <section className="grid overflow-hidden rounded-[1rem] border border-[#e7d5c7] bg-[#fffaf6] divide-y divide-[#e7d5c7] md:grid-cols-2 md:divide-x md:divide-y-0 xl:grid-cols-4">
      {benefits.slice(0, 4).map((item, index) => (
        <div key={`${item.title}-${index}`} className="grid grid-cols-[52px_minmax(0,1fr)] gap-4 px-7 py-6">
          <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#e7d5c7] bg-white text-[#b8735c]">{benefitIcon(item.icon)}</div>
          <div>
            <h3 className="text-sm font-semibold text-[#2f2d2a]">{item.title}</h3>
            <p className="mt-1 text-[13px] leading-5 text-[#5e554d]">{item.description}</p>
          </div>
        </div>
      ))}
    </section>
  );
}

function ShopNewsletterBand({ view }: { view: ShopViewModel }) {
  return (
    <section className="grid overflow-hidden rounded-[1rem] border border-[#e7d5c7] bg-[linear-gradient(90deg,#eef0e6,#fbf4ee_52%,#f4ded4)] lg:grid-cols-[1fr_1.2fr_.95fr]">
      <div className="flex items-center gap-5 px-8 py-8">
        <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-white text-[#b8735c]"><Mail className="h-7 w-7" strokeWidth={1.3} /></div>
        <div>
          <h2 className="font-headline text-2xl text-[#2f2d2a]">{view.newsletter.title}</h2>
          <p className="mt-2 text-[14px] leading-6 text-[#5e554d]">{view.newsletter.description}</p>
        </div>
      </div>
      <NewsletterLeadForm
        idPrefix="shop-newsletter"
        sourceSlug="shop-newsletter"
        sourceName="Shop newsletter"
        offer="shop-newsletter"
        redirectPath="/shop"
        variant="shop"
      />
      <div className="relative hidden min-h-[160px] lg:block">
        <ShopImage src={view.newsletter.imageUrl} alt={view.newsletter.imageAlt} sizes="34vw" />
      </div>
    </section>
  );
}

export function ShopPageClient({
  initialTitles = [],
  initialSettings,
  initialCollections = [],
  checkoutAvailable = false,
  initialTitleCursor = null,
  initialCollectionCursor = null,
}: {
  initialTitles?: TitleDoc[];
  initialSettings?: HomepageSettingsDoc | null;
  initialCollections?: PlatformCollectionDoc[];
  checkoutAvailable?: boolean;
  initialTitleCursor?: string | null;
  initialCollectionCursor?: string | null;
}) {
  const [additionalTitles, setTitles] = useState<TitleDoc[]>([]);
  const [additionalCollections, setCollections] = useState<PlatformCollectionDoc[]>([]);
  const titles = useMemo(() => mergeCatalogRecords(initialTitles, additionalTitles, 'titleId'), [initialTitles, additionalTitles]);
  const collections = useMemo(() => mergeCatalogRecords(initialCollections, additionalCollections, 'collectionId'), [initialCollections, additionalCollections]);
  const [cursors, setCursors] = useState({ titles: initialTitleCursor, collections: initialCollectionCursor });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pending = useRef(false); const epoch = useRef(0);
  useEffect(() => () => { ++epoch.current; }, []);
  async function loadMore(entity: 'titles' | 'collections') {
    if (pending.current || !cursors[entity]) return;
    pending.current = true; const generation = ++epoch.current; setLoading(true); setError('');
    try {
      const response = await fetch(`/api/public/shop?entity=${entity}&cursor=${encodeURIComponent(cursors[entity]!)}`);
      const page = await response.json();
      if (!response.ok) throw new Error(page.error || 'Unable to load products.');
      if (generation !== epoch.current) return;
      if (entity === 'titles') setTitles(current => mergeCatalogRecords(current, page.titles, 'titleId'));
      else setCollections(current => mergeCatalogRecords(current, page.collections, 'collectionId'));
      setCursors(current => ({ ...current, [entity]: page.nextCursor }));
    } catch (cause) { if (generation === epoch.current) setError(cause instanceof Error ? cause.message : 'Unable to load products.'); }
    finally { if (generation === epoch.current) { pending.current = false; setLoading(false); } }
  }
  const { settings: clientSettings } = useHomepageSettings();
  const router = useRouter();
  const settings = initialSettings ?? clientSettings;
  const view = useMemo(
    () => createShopViewModel(titles, settings, collections, { checkoutAvailable }),
    [checkoutAvailable, collections, settings, titles],
  );
  const curatedView = useMemo(() => createShopViewModel(initialTitles, settings, initialCollections, { checkoutAvailable }), [initialTitles, settings, initialCollections, checkoutAvailable]);
  const [category, setCategory] = useState<ShopCategoryId>('all');
  const [sort, setSort] = useState<ShopSortKey>('bestselling');
  const filteredProducts = useMemo(() => sortShopProducts(filterShopProducts(view.products, category), sort), [category, sort, view.products]);

  function handleAddToCart(product: ShopProduct) {
    router.push(product.href);
  }

  return (
    <div className="bg-[#fbf8f3] text-[#2f2d2a]">
      <ShopHero view={view} />
      <main id="shop-books" className="mx-auto max-w-[1340px] px-6 py-8 lg:px-8">
        <div className="grid gap-8 lg:grid-cols-[220px_minmax(0,1fr)]">
          <ShopCategorySidebar view={view} activeCategory={category} onCategoryChange={setCategory} />
          <div className="space-y-8">
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_260px]">
              <FeaturedCollectionCard view={curatedView} />
              <FeaturedBundleCard bundle={curatedView.bundle} onAddBundle={handleAddToCart} />
            </div>
            <ShopToolbar visibleCount={filteredProducts.length} totalCount={view.products.length} sort={sort} onSortChange={setSort} />
            <ShopProductGrid products={filteredProducts} onAddToCart={handleAddToCart} />
            {cursors.titles || cursors.collections || loading || error ? <div className="flex flex-wrap gap-3">{cursors.titles ? <Button variant="outline" disabled={loading} onClick={() => void loadMore('titles')}>Load more books</Button> : null}{cursors.collections ? <Button variant="outline" disabled={loading} onClick={() => void loadMore('collections')}>Load more collections</Button> : null}{loading ? <p role="status">Loading products...</p> : null}{error ? <p role="alert">{error} <Button variant="outline" onClick={() => window.location.reload()}>Refresh shop</Button></p> : null}</div> : null}
          </div>
        </div>
        <div className="mt-8 space-y-6">
          <ShopBenefitStrip benefits={view.benefits} />
          <ShopNewsletterBand view={view} />
        </div>
      </main>
    </div>
  );
}
